import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaBed, FaBath, FaRulerCombined } from "react-icons/fa";
import { motion } from "framer-motion";
import { getAllProperties } from "../../../services/propertyService"; // adjust path
import { API_URL } from "../../../config/api"; 
import { parsePropertyImages } from "../../../utils/admin/parsePropertyImages";

const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const ListingCard = ({ property }) => {
    const statusColor = {
        pending: "bg-yellow-500",
        approved: "bg-green-600",
        rejected: "bg-red-500",
    }[property.status];

    const priceFormatted = property.price?.toLocaleString("vi-VN") + " ₫";

    // Parse images
    const propertyImages = parsePropertyImages(property.images);

    const mainImage =
      propertyImages.length > 0
        ? propertyImages[0]
        : "/no-image.jpg";
    return (
        <motion.div
            variants={cardVariants}
            whileHover={{ y: -5, boxShadow: "0 8px 20px rgba(0,0,0,0.1)" }}
            transition={{ type: "spring", stiffness: 250 }}
            className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition flex flex-col"
        >
            <Link to={`/properties/${property.id}`} className="flex flex-col flex-1">
                <div className="relative">
                    <img
                        loading="lazy"
                        src={mainImage}
                        alt={property.projectName}
                        className="w-full h-48 object-cover"
                    />
                    <span
                        className={`absolute top-2 left-2 text-xs text-white px-2 py-1 rounded ${statusColor}`}
                    >
                        {property.status === "approved"
                            ? "Đã duyệt"
                            : property.status === "pending"
                                ? "Chờ duyệt"
                                : "Từ chối"}
                    </span>
                </div>

                <div className="p-3 text-sm text-blue-900 space-y-1 flex flex-col flex-1">
                    <h3 className="font-semibold text-base line-clamp-2">
                        {property.projectName}
                    </h3>

                    <div className="flex justify-between flex-wrap gap-2 text-gray-700 text-xs">
                        <span className="flex items-center gap-1">
                            <FaBed /> {property.bedrooms} PN
                        </span>
                        <span className="flex items-center gap-1">
                            <FaBath /> {property.bathrooms} PT
                        </span>
                        <span className="flex items-center gap-1">
                            <FaRulerCombined /> {property.area} m²
                        </span>
                    </div>

                    <p className="text-red-600 font-bold text-lg">{priceFormatted}</p>

                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-auto">
                        <FaMapMarkerAlt /> {property.location}
                    </p>
                </div>
            </Link>
        </motion.div>
    );
};



const LatestListings = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const data = await getAllProperties({ status: "approved" }, 1, 12);
                setProperties(data.properties || []);
            } catch (err) {
                console.error(err);
                setError("Lấy dữ liệu thất bại");
            } finally {
                setLoading(false);
            }
        };
        fetchProperties();
    }, []);

    return (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl font-extrabold text-[#0c2c4d] sm:text-4xl">
                        Bất động sản <span className="text-amber-500">Mới nhất</span>
                    </h2>
                    <p className="mt-4 text-lg text-gray-600">
                        Những cơ hội đầu tư và an cư không thể bỏ lỡ vừa được niêm yết.
                    </p>
                </motion.div>

                {loading ? (
                    <p className="text-center text-gray-500">Đang tải dữ liệu...</p>
                ) : error ? (
                    <p className="text-center text-red-500">{error}</p>
                ) : (
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={{
                            visible: { transition: { staggerChildren: 0.15 } },
                        }}
                    >
                        {properties.slice(0, 4).map((property) => (
                            <ListingCard key={property.id} property={property} />
                        ))}
                    </motion.div>
                )}

                <div className="text-center mt-12">
                    <Link
                        to="/overview"
                        className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-red-600 hover:bg-red-700 transition duration-300"
                    >
                        Xem Tất Cả Danh Sách
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default LatestListings;
